import { NgFor, NgIf, AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { MatFormField } from '@angular/material/form-field';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTableDataSource } from '@angular/material/table';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import {
  FilterConfig,
  FilterTypes,
  FiltersConfig,
  PageToolbarComponent,
  FilterComponent,
} from '@next/core-lib/components';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { TableColumn, TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, skipWhile, takeUntil, withLatestFrom } from 'rxjs/operators';
import { COUNTRIES } from 'src/assets/data/Countries';

import { Product } from '../../../shared/models/shared.model';
import * as actions from '../../store/actions/product.actions';
import { upsertProductRequest } from '../../store/actions/product.actions';
import { getProducts, getSelectedCountry } from '../../store/selectors/product.selectors';
import { ProductDetailComponent } from '../product-detail/product-detail.component';

@Component({
  selector: 'app-product-overview',
  templateUrl: './product-overview.component.html',
  styleUrls: ['./product-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PageToolbarComponent,
    FilterComponent,
    MatFormField,
    MatSelect,
    NgFor,
    MatOption,
    NgIf,
    TableComponent,
    MatSlideToggle,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class ProductOverviewComponent implements OnDestroy, AfterViewInit {
  private readonly store = inject<Store<RootState>>(Store);
  private localeService = inject(LocaleService);
  private dialog = inject(DialogService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  private readonly destroyed$ = new Subject<void>();
  private readonly searchChanges$: Subject<string> = new Subject<string>();

  /* TEMPLATES */
  @ViewChild('columnActiveTemplate') columnActiveTemplate: TemplateRef<unknown>;
  @ViewChild('columnTypeTemplate') columnTypeTemplate: TemplateRef<unknown>;

  selectedCountry$: Observable<string> = this.store.select(getSelectedCountry);

  products$: Observable<Product[]> = this.store.select(getProducts).pipe(
    withLatestFrom(this.selectedCountry$),
    skipWhile(([products, _]) => products.length === 0),
    map(([products, selectedCountry]) =>
      products.filter((product) => product.country === selectedCountry),
    ),
  );

  countries: Array<{ id: string; value: string }> = COUNTRIES.sort((a, b) =>
    a.value < b.value ? -1 : 1,
  );

  columns: TableColumn[] = [
    {
      id: 'name',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('product-overview.overview.productName'),
    },
    {
      id: 'code',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('product-overview.overview.productCode'),
    },
    {
      id: 'phase',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('product-overview.overview.phase'),
    },
    {
      id: 'isActive',
      type: TableColumnTypes.TEMPLATE,
      label: this.localeService.translate('product-overview.overview.isActive'),
    },
    {
      id: 'type',
      type: TableColumnTypes.TEMPLATE,
      label: this.localeService.translate('product-overview.overview.productType'),
    },
  ];

  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource<Product>([]),
    columns: this.columns,
    selectColumn: false,
    actionColumn: true,
    pagination: false,
    disableCards: true,
    footer: false,
    scrolling: true,
    wrapperWidth: '100%',
    activeSort: {
      id: 'name',
      start: 'asc',
      disableClear: false,
    },
    actions: [
      {
        id: 'edit',
        callback: (row: Product) => this.openProductEdit(row.id),
        label: this.localeService.translate('edit'),
        icon: 'edit',
        disabled: () => false,
        primary: false,
      },
      {
        id: 'delete',
        callback: (row: Product) => this.deleteProduct(row.id),
        label: this.localeService.translate('delete'),
        icon: 'delete',
        disabled: () => false,
        primary: false,
      },
    ],
  };

  filterConfig: FilterConfig<FiltersConfig> = {
    filters: {
      search: {
        type: FilterTypes.Text,
        changes: this.searchChanges$,
      },
    },
  };

  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    combineLatest([this.products$, this.searchChanges$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([products, query]) => {
        if (!products?.length) {
          this.tableConfig.dataSource.data = [];
        } else if (!query) {
          this.tableConfig.dataSource.data = products;
        } else {
          this.tableConfig.dataSource.data = products.filter(
            (p) =>
              p.name.toLocaleUpperCase().includes(query?.toLocaleUpperCase()) ||
              p.code.toLocaleUpperCase().includes(query?.toLocaleUpperCase()),
          );
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngAfterViewInit() {
    // Set isActive column template.
    const newConfig = { ...this.tableConfig };
    const columnActive = newConfig.columns.find((c) => c.id === 'isActive');
    const columnType = newConfig.columns.find((c) => c.id === 'type');

    columnActive.template = this.columnActiveTemplate;
    columnType.template = this.columnTypeTemplate;
    this.tableConfig = newConfig;

    this.changeDetectorRef.detectChanges();
  }

  createProduct() {
    this.dialog
      .form(ProductDetailComponent, {
        setPendingChanges: true,
        productId: null,
      })
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          this.store.dispatch(upsertProductRequest({ request: result.result.product }));
        }
      });
  }

  openProductEdit(productId: number) {
    this.store.dispatch(
      actions.setSelectedProductRequest({
        id: productId,
      }),
    );
    this.dialog
      .form(ProductDetailComponent, {
        setPendingChanges: false,
        productId,
      })
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          this.store.dispatch(upsertProductRequest({ request: result.result.product }));
        }
      });
  }

  deleteProduct(productId: number) {
    this.store.dispatch(actions.deleteProductWithConfirmation({ productId }));
  }

  changeCountrySelection(selection: MatSelectChange) {
    this.store.dispatch(actions.setSelectedCountryRequest({ id: selection.value as string }));
  }

  onToggleActive(product: Product) {
    const newProduct = { ...product };
    newProduct.isActive = !product.isActive;
    this.store.dispatch(actions.upsertProductRequest({ request: newProduct }));
  }
}
